import { ReactNode } from 'react';
import { Container, Stack, Typography } from '@optimus-web/core';
import { IconName } from '@optimus-web/icons';
import {
    ItemDefaultIconElement,
    itemDefaultIconRadiusToken,
    ItemElement,
    ItemIconElement,
    itemIconRadiusToken,
    ItemLineElement,
    ItemTextWrapper,
} from './style';

export type LogItemVariant = 'basic' | 'success' | 'warning' | 'danger';

export interface LogItemProps {
    ariaLabel?: string;
    key: string;
    icon?: IconName;
    variant?: LogItemVariant;
    stepName: ReactNode;
    amount?: ReactNode;
    description?: ReactNode;
    date?: ReactNode;
    link?: ReactNode;
}

type LogItemInternalProps = Omit<LogItemProps, 'key'> & {
    last: boolean;
};

export const LogItem = ({
    ariaLabel,
    last,
    icon,
    variant = 'basic',
    stepName,
    description,
    amount,
    date,
    link,
}: LogItemInternalProps) => (
    <Container padding={['0', '0', '0', icon ? itemIconRadiusToken : itemDefaultIconRadiusToken]}>
        <ItemElement aria-label={ariaLabel} {...(variant && { [`data-test-activity-log-item-${variant}`]: true })}>
            <Stack vertical alignment="start" itemAlignment="center">
                {icon ? (
                    <Container padding={[itemIconRadiusToken, '0', '0', '0']}>
                        <ItemIconElement value={icon} size="small" variant={variant} />
                    </Container>
                ) : (
                    <Container padding={[itemDefaultIconRadiusToken, '0', '0', '0']}>
                        <ItemDefaultIconElement variant={variant} />
                    </Container>
                )}
                {!last && <ItemLineElement aria-hidden isDefaultIcon={!icon} />}
            </Stack>

            <ItemTextWrapper>
                <Container padding={['0', '600', '0', '0']}>
                    <Stack spacing="50" vertical>
                        <Stack spacing="25" vertical>
                            <Typography textStyle="bodyMediumStrong" as="div">
                                {stepName}
                            </Typography>
                            {amount && (
                                <Typography textStyle="bodyMediumStrong" as="div">
                                    {amount}
                                </Typography>
                            )}
                            {description && (
                                <Typography textStyle="bodySmall" variant="tertiary" as="div">
                                    {description}
                                </Typography>
                            )}
                            {date && (
                                <Typography textStyle="bodySmall" variant="tertiary" as="time">
                                    {date}
                                </Typography>
                            )}
                        </Stack>
                        {link && (
                            <Typography textStyle="bodyMediumStrong" as="div">
                                {link}
                            </Typography>
                        )}
                    </Stack>
                </Container>
            </ItemTextWrapper>
        </ItemElement>
    </Container>
);
